import { formatDate } from '@angular/common';
import {
  Component,
  DOCUMENT,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import {
  IExtendedTranscriptChunk,
  TranscriptService,
} from '@techspert-io/transcriptions';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs';
import type { Cell, Sheet, SheetData } from 'write-excel-file/browser';
import { WorksheetService } from '../../../../public/opportunity/services/worksheet.service';
import { FileStoreService } from '../../../../shared/services/filestore.service';
import {
  DataCaptureSummaryStatus,
  IDataCaptureSegment,
  IDataCaptureSheet,
  IDataCaptureSummaryCell,
  IDataCaptureSummarySubTheme,
  IDataCaptureSummaryTheme,
  IRespondent,
  IRespondentAnswerReference,
  IRespondentFile,
  IRespondentWithAnswers,
  RespondentSource,
} from '../../models/data-capture.models';
import { MAX_SHEET_NAME_LENGTH } from '../../validators/sheet-name.validators';
import { DataCaptureSheetService } from '../../services/data-capture-sheet.service';
import { DataCaptureService } from '../../services/data-capture.service';
import {
  assignDepthAndNotation,
  orderQuestionsForDisplay,
} from '../../utils/question-numbering.util';
import { AddRespondentComponent } from '../add-respondent/add-respondent.component';
import { DeleteSheetConfirmationComponent } from '../delete-sheet-confirmation/delete-sheet-confirmation.component';

interface IDisplaySheet extends IDataCaptureSheet {
  selected?: boolean;
}

type OpenCitation = {
  respondent: IRespondent;
  citation: IRespondentAnswerReference;
};

const MIN_EXPORT_RESPONDENT_ROW_HEIGHT = 50;
const EXPORT_ROW_HEIGHT_PADDING = 8;

@Component({
  selector: 'app-data-capture',
  templateUrl: './data-capture.component.html',
  styleUrls: ['./data-capture.component.scss'],
  standalone: false,
})
export class DataCaptureComponent implements OnInit, OnDestroy {
  @Input() opportunityId: string;
  @Input() publicDisplayId: string;

  availableSheets: IDisplaySheet[] = [];

  selectedSheet: IDisplaySheet;

  segments: IDataCaptureSegment[] = [];

  loading = true;

  downloadingCsv = false;
  deletingSheet = false;

  audioStartTime = 0;
  transcriptFile?: IRespondentFile;
  respondent: IRespondent;

  audioData?: string;
  autoScroll = true;
  transcriptChunks: IExtendedTranscriptChunk[] = [];

  viewingCitation = false;
  loadingCitation = false;
  isFullscreen: boolean;

  get sheetNames() {
    return this.availableSheets.map((s) => s.name);
  }

  get transcriptFileName() {
    const fileName = this.transcriptFile
      ? this.transcriptFile.fileName
      : `${this.respondent.firstName} - ${this.respondent.lastName}`;

    return fileName.trim();
  }

  constructor(
    private dialog: MatDialog,
    private dataCaptureService: DataCaptureService,
    private dataCaptureSheetService: DataCaptureSheetService,
    private worksheetService: WorksheetService,
    private toastService: ToastrService,
    private fileStoreService: FileStoreService,
    private analyticsEventsService: AnalyticsEventsService,
    private transcriptService: TranscriptService,
    private router: Router,
    private renderer: Renderer2,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit() {
    this.refreshDataCapture().subscribe();
  }

  ngOnDestroy() {
    this.revokeAudioData();
  }

  toggleFullscreen() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  downloadCsv(sheet?: IDataCaptureSheet) {
    if (!this.availableSheets.length) {
      this.toastService.warning('Set up your first sheet', 'No data to export');
      return;
    }

    this.analyticsEventsService.sendEvent('export', {
      event_category: 'data_capture',
      event_label: sheet ? sheet.name : 'all_sheets',
      value: sheet ? sheet.data_capture_sheet_id : this.opportunityId,
    });

    if (sheet) {
      this.fetchSheetExportData$(sheet)
        .pipe(
          map(({ respondents, summaries }) =>
            this.formatWorkbook(
              sheet,
              respondents,
              summaries,
              this.getUniqueExportSheetNames([sheet.name])[0]
            )
          ),
          switchMap((exportSheet) =>
            this.worksheetService.save([exportSheet], `${sheet.name}-export`)
          ),
          catchError((err) => {
            this.toastService.error(
              'Please try again',
              'Error exporting sheet'
            );
            return err;
          })
        )
        .subscribe();
    } else {
      this.downloadingCsv = true;

      forkJoin(
        this.availableSheets.map((sheet) =>
          this.fetchSheetExportData$(sheet).pipe(
            map(({ respondents, summaries }) => ({
              sheet,
              respondents,
              summaries,
            }))
          )
        )
      )
        .pipe(
          map((res) => {
            const exportSheetNames = this.getUniqueExportSheetNames(
              res.map(({ sheet }) => sheet.name)
            );

            return res.map((sheetRes, index) =>
              this.formatWorkbook(
                sheetRes.sheet,
                sheetRes.respondents,
                sheetRes.summaries,
                exportSheetNames[index]
              )
            );
          }),
          switchMap((exportSheets) =>
            this.worksheetService.save(exportSheets, 'data-capture-export')
          ),
          catchError((err) => {
            this.toastService.error(
              'Please try again',
              'Error exporting sheets'
            );
            return err;
          }),
          finalize(() => (this.downloadingCsv = false))
        )
        .subscribe();
    }
  }

  addRespondent() {
    this.dialog
      .open<AddRespondentComponent, string, boolean>(AddRespondentComponent, {
        width: '1100px',
        maxWidth: '95vw',
        panelClass: 'add-respondent-dialog-panel',
        autoFocus: false,
        data: this.opportunityId,
      })
      .afterClosed()
      .pipe(
        takeWhile((res) => !!res),
        tap(() => {
          this.analyticsEventsService.sendEvent('add_respondent', {
            event_category: 'data_capture',
            event_label: 'add_respondent',
            value: this.opportunityId,
          });
        }),
        tap(() => (this.loading = true)),
        switchMap(() => this.refreshDataCapture())
      )
      .subscribe();
  }

  edit(sheet: IDataCaptureSheet) {
    this.router.navigate([
      '/connect',
      'data-capture',
      this.publicDisplayId,
      sheet.data_capture_sheet_id,
      'edit',
    ]);
  }

  duplicateSheet(sheet: IDataCaptureSheet) {
    if (this.segments.every((s) => s.occupied)) {
      this.toastService.warning(
        'No outstanding segments available',
        'Edit one of your existing sheets'
      );
      return;
    }

    this.dataCaptureSheetService.sheetDefaults = {
      name: `${sheet.name} (COPY)`,
      additional_export_headers: sheet.additional_export_headers,
      answer_format: sheet.answer_format,
      discussion_guide: sheet.discussion_guide,
      suggested_question_threshold: sheet.suggested_question_threshold,
    };

    this.router.navigate([
      '/connect',
      'data-capture',
      this.publicDisplayId,
      'new-sheet',
    ]);
  }

  deleteSheet(sheet: IDataCaptureSheet) {
    this.dialog
      .open<DeleteSheetConfirmationComponent, IDataCaptureSheet, boolean>(
        DeleteSheetConfirmationComponent,
        {
          width: '500px',
          autoFocus: false,
          data: sheet,
        }
      )
      .afterClosed()
      .pipe(
        filter((res) => !!res),
        tap(() => {
          this.deletingSheet = true;
          this.viewingCitation = false;
        }),
        switchMap(() =>
          this.dataCaptureService.deleteSheet(sheet.data_capture_sheet_id)
        ),
        tap(() => {
          this.availableSheets = this.availableSheets.filter(
            (s) => s.data_capture_sheet_id !== sheet.data_capture_sheet_id
          );
          this.selectedSheet =
            this.selectedSheet.data_capture_sheet_id ===
            sheet.data_capture_sheet_id
              ? this.availableSheets.find(Boolean)
              : this.selectedSheet;
          this.segments = this.segments.map((s) =>
            sheet.opportunity_segment_ids.includes(s.segmentId)
              ? { ...s, occupied: false }
              : s
          );
        }),
        catchError((err) => {
          this.toastService.error('Please try again', 'Error removing sheet');
          return err;
        }),
        finalize(() => (this.deletingSheet = false))
      )
      .subscribe();
  }

  selectSheet(sheet: IDataCaptureSheet) {
    this.selectedSheet = sheet;
  }

  handleSheetUpdated(updatedSheet: IDataCaptureSheet) {
    this.availableSheets = this.availableSheets.map((sheet) =>
      sheet.data_capture_sheet_id === updatedSheet.data_capture_sheet_id
        ? updatedSheet
        : sheet
    );

    if (
      this.selectedSheet?.data_capture_sheet_id ===
      updatedSheet.data_capture_sheet_id
    ) {
      this.selectedSheet = updatedSheet;
    }
  }

  newSheet() {
    const unassignedSegments = this.segments.filter((s) => !s.occupied);

    if (!unassignedSegments.length) {
      this.toastService.warning(
        'No outstanding segments available',
        'Edit one of your existing sheets'
      );
      return;
    }

    this.router.navigate([
      '/connect',
      'data-capture',
      this.publicDisplayId,
      'new-sheet',
    ]);
  }

  openCitation(citation: OpenCitation) {
    this.viewingCitation = true;
    this.loadingCitation = true;
    this.loadResources(citation.respondent, citation.citation);
  }

  closeCitation() {
    this.viewingCitation = false;
  }

  private fetchSheetExportData$(sheet: IDataCaptureSheet) {
    return forkJoin({
      respondents:
        this.dataCaptureService.getRespondentsWithAnswersByDataCaptureSheetId(
          sheet.data_capture_sheet_id
        ),
      summaries: this.dataCaptureService.getSummariesByDataCaptureSheetId(
        sheet.data_capture_sheet_id,
        sheet.discussion_guide.map((question) => question.question_id)
      ),
    });
  }

  private enterFullscreen() {
    this.isFullscreen = true;
    this.renderer.addClass(this.document.body, 'data-capture-fullscreen');
  }

  private exitFullscreen() {
    this.isFullscreen = false;
    this.renderer.removeClass(this.document.body, 'data-capture-fullscreen');
  }

  private refreshDataCapture() {
    return combineLatest([
      this.dataCaptureService.querySheetsByOpportunityId(this.opportunityId),
      this.dataCaptureService.getAvailableSegments(this.opportunityId),
    ]).pipe(
      tap(([sheets, segments]) => {
        this.availableSheets = sheets.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        this.selectedSheet = this.availableSheets.find(Boolean);

        this.segments = segments;
        this.loading = false;
      })
    );
  }

  private loadResources(
    respondent: IRespondent,
    citation: IRespondentAnswerReference
  ) {
    const transcriptFile = respondent.processedTranscript;
    const audioFile = respondent.audio;

    this.revokeAudioData();
    this.transcriptChunks = [];
    this.transcriptFile = undefined;

    const audioRequest$ = audioFile
      ? this.fileStoreService.getExpertAudioFileContents(audioFile.fileKey)
      : of(null);

    combineLatest([
      this.transcriptService.getTranscriptChunks(respondent.respondentId),
      audioRequest$,
    ]).subscribe(([content, audio]) => {
      this.transcriptChunks = content;
      this.transcriptFile = transcriptFile;
      this.respondent = respondent;

      if (audio) {
        this.audioData = URL.createObjectURL(audio);
      }

      this.audioStartTime = citation ? citation.timestampStart / 1000 : 0;

      this.loadingCitation = false;
    });
  }

  private revokeAudioData() {
    if (this.audioData) {
      URL.revokeObjectURL(this.audioData);
      this.audioData = undefined;
    }
  }

  private getUniqueExportSheetNames(sheetNames: string[]): string[] {
    const usedNames = new Set<string>();

    return sheetNames.map((sheetName) => {
      let exportName = sheetName.slice(0, MAX_SHEET_NAME_LENGTH);
      let suffixNumber = 2;

      while (usedNames.has(exportName.toLowerCase())) {
        const suffix = ` (${suffixNumber++})`;
        exportName = `${sheetName.slice(
          0,
          MAX_SHEET_NAME_LENGTH - suffix.length
        )}${suffix}`;
      }

      usedNames.add(exportName.toLowerCase());
      return exportName;
    });
  }

  private formatWorkbook(
    sheet: IDataCaptureSheet,
    respondents: IRespondentWithAnswers[],
    summaries: Record<string, IDataCaptureSummaryCell>,
    exportSheetName: string
  ): Sheet<Blob> {
    const groupedThemes = this.groupQuestionsByTheme(sheet);
    const expertBioColumnIndex = this.getExpertBioColumnIndex(sheet);

    const additionalColumns = [
      sheet.additional_export_headers.geography ? 'Geography' : '',
      sheet.additional_export_headers.profile_type ? 'Profile type' : '',
      sheet.additional_export_headers.respondent_source
        ? 'Recruitment source'
        : '',
      sheet.additional_export_headers.date_and_time_of_call
        ? 'Date and time of call'
        : '',
      sheet.additional_export_headers.expert_bio ? 'Expert biography' : '',
    ].filter(Boolean);

    const questionHeaders = groupedThemes.flatMap((theme) =>
      Object.values(theme.questions).map(
        (qDetails) => `Q${qDetails.numberNotation}. ${qDetails.question}`
      )
    );
    const themeRow: Cell[] = [null, ...additionalColumns.map(() => null)];

    groupedThemes.forEach((theme, index) => {
      const questionCount = Object.keys(theme.questions).length;
      themeRow.push({
        value: theme.theme,
        ...(questionCount > 1 ? { columnSpan: questionCount } : {}),
        align: 'center',
        alignVertical: 'center',
        fontWeight: 'bold',
        textColor: '#FFFFFF',
        backgroundColor: index % 2 ? '#49E5FD' : '#1E2B4E',
      });
      themeRow.push(...Array.from({ length: questionCount - 1 }, () => null));
    });

    const headerRow: Cell[] = [
      'Respondent',
      ...additionalColumns,
      ...questionHeaders,
    ].map((value, index) => ({
      value,
      wrap: true,
      align: 'center',
      alignVertical: 'center',
      fontWeight: 'bold',
      bottomBorderStyle: 'thin',
      ...(index > 0 ? { backgroundColor: '#F2F2F2' } : {}),
    }));

    const data: SheetData = [themeRow, headerRow];

    for (const respondent of respondents) {
      const mappedAnswers = groupedThemes.flatMap((theme) =>
        Object.values(theme.questions).map(
          (qDetails) => respondent.answers[qDetails.question_id]?.answer || ''
        )
      );

      const additionalColumns = [
        sheet.additional_export_headers.geography
          ? respondent.geography
          : false,
        sheet.additional_export_headers.profile_type
          ? respondent.segment
          : false,
        sheet.additional_export_headers.respondent_source
          ? respondent.type === RespondentSource.Techspert
            ? 'Techspert'
            : respondent.source
          : false,
        sheet.additional_export_headers.date_and_time_of_call
          ? this.formatDate(respondent.callDate)
          : false,
        sheet.additional_export_headers.expert_bio
          ? this.stripHTML(respondent.bio)
          : false,
      ].filter((value): value is string => typeof value === 'string');

      const rowValues = [
        respondent.firstName + ' ' + respondent.lastName,
        ...additionalColumns,
        ...mappedAnswers,
      ];
      const rowHeight = Math.max(
        MIN_EXPORT_RESPONDENT_ROW_HEIGHT,
        this.getExportRowHeight(rowValues)
      );

      data.push(
        rowValues.map((value, index): Cell => ({
          value,
          ...(index === 0 ? { fontWeight: 'bold', height: rowHeight } : {}),
          wrap: true,
          alignVertical: 'center',
          align: index + 1 === expertBioColumnIndex ? 'left' : 'center',
        }))
      );
    }

    if (this.shouldExportSummaries(summaries)) {
      const summaryValues = [
        'Summary',
        ...additionalColumns.map(() => ''),
        ...groupedThemes.flatMap((theme) =>
          theme.questions.map((question) =>
            this.formatSummaryCell(summaries[question.question_id])
          )
        ),
      ];
      const rowHeight = this.getExportRowHeight(summaryValues, true);

      data.push(
        summaryValues.map((value, index): Cell => ({
          value,
          ...(index === 0 ? { fontWeight: 'bold', height: rowHeight } : {}),
          wrap: true,
          alignVertical: index === 0 ? 'center' : 'top',
          align: index === 0 ? 'center' : 'left',
          ...(index > 0 ? { indent: 1 } : {}),
        }))
      );
    }

    return {
      sheet: exportSheetName,
      data,
      columns: headerRow.map(() => ({ width: 45 })),
    };
  }

  private getExportRowHeight(values: string[], indentAfterFirst = false) {
    const maxLineCount = values.reduce((maxLines, value, index) => {
      if (!value) {
        return maxLines;
      }

      const indent = indentAfterFirst && index > 0 ? 1 : 0;

      const availableWidth = Math.max(1, Math.floor(45 * 1.15) - indent * 2);

      const lineCount = value
        .split('\n')
        .reduce(
          (total, line) =>
            total +
            Math.max(1, Math.ceil(line.trimEnd().length / availableWidth)),
          0
        );

      return Math.max(maxLines, lineCount);
    }, 1);

    return 15 * maxLineCount + EXPORT_ROW_HEIGHT_PADDING;
  }

  private getExpertBioColumnIndex(sheet: IDataCaptureSheet) {
    if (!sheet.additional_export_headers.expert_bio) {
      return null;
    }

    const enabledColumnsBeforeBio = [
      sheet.additional_export_headers.geography,
      sheet.additional_export_headers.profile_type,
      sheet.additional_export_headers.respondent_source,
      sheet.additional_export_headers.date_and_time_of_call,
    ].filter(Boolean).length;

    return 2 + enabledColumnsBeforeBio;
  }

  private formatDate(date: string) {
    const d = new Date(date);

    const day = d.getDate();
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    const hours = d.getHours();
    const minutes = d.getMinutes();

    const ampm = hours >= 12 ? 'pm' : 'am';

    return `${day} ${month} ${year}, ${hours}:${minutes
      .toString()
      .padStart(2, '0')} ${ampm}`;
  }

  private groupQuestionsByTheme(sheet: IDataCaptureSheet) {
    const exportableQuestions = sheet.discussion_guide.filter(
      (q) => !q.suggestion_status || q.suggestion_status === 'approved'
    );

    const themes = [
      ...new Set(exportableQuestions.map((question) => question.theme)),
    ];

    const numberedQuestions = assignDepthAndNotation(
      themes.flatMap((theme) =>
        orderQuestionsForDisplay(
          exportableQuestions.filter((question) => question.theme === theme)
        )
      )
    );

    return themes.map((theme) => ({
      theme,
      questions: numberedQuestions.filter(
        (question) => question.theme === theme
      ),
    }));
  }

  private shouldExportSummaries(
    summaries: Record<string, IDataCaptureSummaryCell>
  ) {
    return Object.values(summaries || {}).some((summary) =>
      [
        DataCaptureSummaryStatus.Processing,
        DataCaptureSummaryStatus.Processed,
      ].includes(summary.status)
    );
  }

  private formatSummaryCell(summary?: IDataCaptureSummaryCell) {
    if (!summary) {
      return '-';
    }

    if (summary.status === DataCaptureSummaryStatus.Processing) {
      return 'Processing';
    }

    if (
      summary.status !== DataCaptureSummaryStatus.Processed ||
      !summary.summary?.key_themes?.length
    ) {
      return '-';
    }

    const summaryText = summary.summary.key_themes
      .map((theme) => this.formatSummaryTheme(theme))
      .filter(Boolean)
      .join('\n\n');

    const createdAt = summary.updated_at
      ? `Created ${this.formatSummaryDate(summary.updated_at)}`
      : '';

    return [summaryText, createdAt].filter(Boolean).join('\n\n');
  }

  private formatSummaryTheme(theme: IDataCaptureSummaryTheme) {
    const subThemes = (theme.sub_themes || [])
      .map((subTheme) => this.formatSummarySubTheme(subTheme))
      .filter(Boolean)
      .join('\n\n');

    return [
      `${theme.title} (${theme.respondent_count})`,
      theme.summary || '',
      subThemes,
    ]
      .filter(Boolean)
      .join('\n');
  }

  private formatSummarySubTheme(subTheme: IDataCaptureSummarySubTheme) {
    const quotes = (subTheme.quotes || [])
      .map((quote) => `Quote: ${quote.quote}`)
      .join('\n');

    return [
      `- ${subTheme.title}`,
      subTheme.summary ? `  ${subTheme.summary}` : '',
      quotes ? `  ${quotes.replace(/\n/g, '\n  ')}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  private formatSummaryDate(date: string) {
    return `${formatDate(date, 'd MMM yy, h:mm a', this.locale)} (${this.locale})`;
  }

  private stripHTML(html: string): string {
    const normalizedHtml = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|ul|ol|h[1-6]|tr)>/gi, '$&\n');

    const doc = new DOMParser().parseFromString(normalizedHtml, 'text/html');

    return (doc.body.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
}
